/**
 * Participant Payment Service
 * 
 * Frontend service for managing participant-level payments
 */

import { apiClient } from './api';

export interface ParticipantPayment {
  id: string;
  quoteId: string;
  quoteNumber: string;
  quoteState: string;
  participantId: string;
  participantName: string;
  participantType: 'farmer' | 'organization';
  totalAmountCents: number;
  paidAmountCents: number;
  remainingAmountCents: number;
  status: 'pending' | 'authorized' | 'partial' | 'completed' | 'failed';
  authorizationId?: string;
  paymentProviderId?: string;
  paymentDetails?: Record<string, unknown>;
  lastPaymentAt?: string;
  createdAt: string;
  updatedAt: string;
  installmentCount: number;
  totalInstallmentAmount: number;
}

export interface PaymentInstallment {
  id: string;
  amountCents: number;
  status: 'pending' | 'completed' | 'failed';
  transactionId?: string;
  processedAt?: string;
  webhookData?: Record<string, unknown>;
  createdAt: string;
}

export interface ParticipantPaymentDetails extends ParticipantPayment {
  quotePremium: string;
  productName: string;
  participantDetails: {
    farmer?: Record<string, unknown>;
    organization?: Record<string, unknown>;
  };
  installments: PaymentInstallment[];
}

export interface ParticipantPaymentFilters {
  page?: number;
  limit?: number;
  status?: string;
  quoteNumber?: string;
  participantName?: string;
  organizationId?: string;
}

export interface ParticipantPaymentListResponse {
  payments: ParticipantPayment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class ParticipantPaymentService {
  /**
   * List participant payments with filtering and pagination
   */
  async listParticipantPayments(filters: ParticipantPaymentFilters = {}): Promise<ParticipantPaymentListResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);
    if (filters.quoteNumber) params.append('quoteNumber', filters.quoteNumber);
    if (filters.participantName) params.append('participantName', filters.participantName);
    if (filters.organizationId) params.append('organizationId', filters.organizationId);

    const response = await apiClient.getParticipantPayments(params);
    
    // Return the API response (cast via unknown for type compatibility)
    return response as unknown as ParticipantPaymentListResponse;
  }

  /**
   * Get detailed participant payment information
   */
  async getParticipantPayment(id: string): Promise<ParticipantPaymentDetails> {
    const response = await apiClient.getParticipantPaymentById(id);
    
    // Return the API response (cast via unknown for type compatibility)
    return response as unknown as ParticipantPaymentDetails;
  }

  /**
   * Retry failed payment
   */
  async retryPayment(id: string): Promise<{
    message: string;
    authorizationId: string;
    status: string;
  }> {
    return await apiClient.retryParticipantPayment(id);
  }

  /**
   * Format amount from cents to currency string
   */
  formatAmount(amountCents: number): string {
    return `$${(amountCents / 100).toFixed(2)}`;
  }

  /**
   * Get status badge color
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'completed':
        return 'success';
      case 'authorized':
        return 'info';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      case 'pending':
      default:
        return 'default';
    }
  }

  /**
   * Get status display text
   */
  getStatusText(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'authorized':
        return 'Authorized';
      case 'partial':
        return 'Partial';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }
}

export const participantPaymentService = new ParticipantPaymentService();
